// Code-drawn previews for the "What you can make" cards: one small scene per format, drawn the way that format's
// video looks (a documentary's lower third and source chip, a storybook subtitle, a trailer title card...). They
// show until a real sample is showcased for the format (GET /api/formats -> sample), and they stay as the poster
// fallback after that. Each is an SVG on a 320x200 canvas, sliced to fill its frame: the featured cards crop to 4:3,
// so anything that matters sits between x=28 and x=292. Hovering a card runs its `.lp-art-*` animation (landing.css);
// reduced motion turns every one of them off. Decorative: the card itself names the format.
import type { ReactNode } from "react";

const W = 320;
const H = 200;

function Svg({ id, children, bg }: { id: string; children: ReactNode; bg: string }) {
  return (
    <svg className={`lp-art lp-art-${id}`} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">
      <rect width={W} height={H} fill={bg} />
      {children}
    </svg>
  );
}

function rng(seed: number) {
  let s = seed >>> 0 || 1;
  return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 2 ** 32);
}

/* ---------------------------------------------------------------- Animated documentary */

function Documentary() {
  // a logbook page under a slow push-in: a moth taped beside the day's entries; letterbox, lower third, source chip
  const r = rng(11);
  const lines = Array.from({ length: 9 }, (_, i) => {
    const y = 38 + i * 13;
    let d = `M${74 + r() * 6} ${y}`;
    const end = 150 + r() * 90;
    for (let x = 84; x < end; x += 7) d += ` q3 ${(r() * 3 - 1.5).toFixed(1)} 7 ${(r() * 1.4 - 0.7).toFixed(1)}`;
    return <path key={i} d={d} />;
  });
  return (
    <Svg id="documentary" bg="#1b1410">
      <defs>
        <radialGradient id="doc-light" cx=".42" cy=".38" r=".75">
          <stop offset="0" stopColor="#f6ddb0" stopOpacity=".55" />
          <stop offset=".6" stopColor="#7a4b25" stopOpacity=".15" />
          <stop offset="1" stopColor="#000" stopOpacity=".6" />
        </radialGradient>
        <linearGradient id="doc-page" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#e7d3ad" />
          <stop offset="1" stopColor="#c7a674" />
        </linearGradient>
      </defs>
      <g className="lp-art-push">
        <rect width={W} height={H} fill="#2a1d14" />
        <g transform="rotate(-4 160 100)">
          <rect x="52" y="16" width="226" height="170" rx="3" fill="url(#doc-page)" />
          <path d="M52 16h226v170H52z" fill="none" stroke="#8a6a42" strokeOpacity=".5" />
          <g stroke="#a98a5e" strokeOpacity=".35" strokeWidth=".6">
            {Array.from({ length: 11 }, (_, i) => (
              <line key={i} x1="58" x2="272" y1={30 + i * 13} y2={30 + i * 13} />
            ))}
            <line x1="70" x2="70" y1="18" y2="184" stroke="#9c4b3c" strokeOpacity=".45" />
          </g>
          <g fill="none" stroke="#3b2a1d" strokeOpacity=".72" strokeWidth="1.1" strokeLinecap="round">
            {lines}
          </g>
          {/* the moth, taped down */}
          <g transform="translate(222 98) rotate(14)">
            <path d="M0 0c-10-12-30-14-34-4-3 9 13 14 34 6z" fill="#6f5640" />
            <path d="M0 0c10-12 30-14 34-4 3 9-13 14-34 6z" fill="#76604a" />
            <path d="M0 2c-8 6-22 14-24 22 6 2 18-6 24-18z" fill="#5d4735" />
            <path d="M0 2c8 6 22 14 24 22-6 2-18-6-24-18z" fill="#655040" />
            <ellipse cx="0" cy="4" rx="2.6" ry="11" fill="#3a2a1e" />
            <path d="M-1-6c-3-6-7-9-10-10M1-6c3-6 7-9 10-10" stroke="#3a2a1e" strokeWidth=".8" fill="none" />
            <rect x="-30" y="-8" width="22" height="9" fill="#f5ecd6" opacity=".5" transform="rotate(-24)" />
            <rect x="9" y="12" width="22" height="9" fill="#f5ecd6" opacity=".5" transform="rotate(-24)" />
          </g>
        </g>
        <rect width={W} height={H} fill="url(#doc-light)" />
      </g>
      {/* letterbox */}
      <rect width={W} height="14" fill="#000" />
      <rect y={H - 14} width={W} height="14" fill="#000" />
      {/* lower third */}
      <g transform="translate(34 128)">
        <rect width="2" height="30" fill="#ffb23e" />
        <text x="10" y="13" className="t-serif" fontSize="14" fill="#f8efe0">
          The moth in the machine
        </text>
        <text x="10" y="27" className="t-sans" fontSize="7.5" fontWeight="600" fill="#f1e4cc">
          Chapter one
        </text>
      </g>
      <g transform="translate(34 166)">
        <rect width="98" height="13" rx="6.5" fill="#000" fillOpacity=".55" />
        <text x="8" y="9" className="t-sans" fontSize="6.4" fill="#e9dcc6">
          Source: operator's logbook
        </text>
      </g>
    </Svg>
  );
}

/* ---------------------------------------------------------------- Cartoon or short story */

function Cartoon() {
  const r = rng(5);
  const flies = Array.from({ length: 9 }, (_, i) => ({ x: 40 + r() * 240, y: 60 + r() * 70, s: 0.9 + r() * 1.3, d: (i % 4) * 0.6 }));
  const stars = Array.from({ length: 22 }, () => ({ x: r() * W, y: r() * 90, o: 0.3 + r() * 0.6 }));
  return (
    <Svg id="cartoon" bg="#1d2550">
      <defs>
        <linearGradient id="ct-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1a1f4b" />
          <stop offset=".65" stopColor="#3b4d8a" />
          <stop offset="1" stopColor="#6d7fb4" />
        </linearGradient>
        <radialGradient id="ct-moon" cx=".5" cy=".5" r=".5">
          <stop offset=".45" stopColor="#fff4d6" stopOpacity=".5" />
          <stop offset="1" stopColor="#fff4d6" stopOpacity="0" />
        </radialGradient>
        <filter id="ct-wash" x="-5%" y="-5%" width="110%" height="110%">
          <feTurbulence type="fractalNoise" baseFrequency=".035" numOctaves="2" seed="3" />
          <feDisplacementMap in="SourceGraphic" scale="5" />
        </filter>
      </defs>
      <rect width={W} height={H} fill="url(#ct-sky)" />
      {stars.map((s, i) => (
        <circle key={i} cx={s.x} cy={s.y} r=".8" fill="#fff" opacity={s.o} />
      ))}
      <g className="lp-art-glow">
        <circle cx="228" cy="52" r="44" fill="url(#ct-moon)" />
      </g>
      <circle cx="228" cy="52" r="19" fill="#fff3d0" />
      <circle cx="221" cy="47" r="3.4" fill="#eadbb0" />
      <circle cx="233" cy="58" r="2.2" fill="#eadbb0" />
      <g filter="url(#ct-wash)">
        <path d="M-10 132c50-26 110-30 170-14s110 6 170-16v110H-10z" fill="#33406f" />
        <path d="M-10 150c60-20 120-12 170 2 60 16 120 4 170-10v70H-10z" fill="#263159" />
        <path d="M-10 166c70-14 150-8 200 4s100 6 140-4v50H-10z" fill="#1b2344" />
      </g>
      {/* the fox, sitting on the near hill, looking up at the moon */}
      <g transform="translate(120 140)">
        <path d="M14 22c18 2 30-6 28-18-1-8-9-8-12-2 3 6-2 12-14 12z" fill="#e0773a" />
        <path d="M40 4c-2-5-7-6-10-2 3 3 5 5 10 2z" fill="#fbe9d6" />
        <path d="M0 24c-3-12 0-24 8-30l12 2c6 10 6 22 2 28z" fill="#e88444" />
        <path d="M6 22c-1-8 1-14 5-18 4 4 6 10 5 18z" fill="#fbe9d6" />
        <path d="M5-4c-2-8 0-16 6-20l4 12z" fill="#e88444" />
        <path d="M19-2c4-8 2-16-2-20l-6 12z" fill="#d96f34" />
        <path d="M4-6l-5-12 9 7zM18-6l6-12-9 6z" fill="#c95f2b" />
        <circle cx="9" cy="-10" r="1.3" fill="#1a1208" />
        <circle cx="16" cy="-10" r="1.3" fill="#1a1208" />
        <path d="M11-5l1.6 1.6L14-5z" fill="#1a1208" />
      </g>
      {flies.map((f, i) => (
        <circle key={i} className="lp-art-twinkle" style={{ animationDelay: `${f.d}s` }} cx={f.x} cy={f.y} r={f.s} fill="#ffe08a" />
      ))}
      {/* subtitle with the speaker's name */}
      <g transform="translate(160 178)">
        <rect x="-86" y="-11" width="172" height="17" rx="4" fill="#0c0f22" fillOpacity=".6" />
        <text x="0" y="1" textAnchor="middle" className="t-sans" fontSize="8.6" fill="#fff8ec">
          <tspan fill="#ffa25c" fontWeight="700">
            Fen:{" "}
          </tspan>
          Is anybody else awake?
        </text>
      </g>
      {/* the page corner */}
      <path d={`M${W} ${H - 30}L${W - 30} ${H}H${W}z`} fill="#f2e6cf" />
      <path d={`M${W} ${H - 30}L${W - 30} ${H}l6-14z`} fill="#d9c7a6" />
    </Svg>
  );
}

/* ---------------------------------------------------------------- Short film or trailer */

function ShortFilm() {
  const r = rng(9);
  const stars = Array.from({ length: 50 }, () => ({ x: r() * W, y: 24 + r() * 150, o: 0.2 + r() * 0.7, s: 0.4 + r() * 0.7 }));
  return (
    <Svg id="short-film" bg="#05070c">
      <defs>
        <radialGradient id="sf-neb" cx=".7" cy=".45" r=".7">
          <stop offset="0" stopColor="#2f6f8f" stopOpacity=".55" />
          <stop offset=".45" stopColor="#3b2256" stopOpacity=".35" />
          <stop offset="1" stopColor="#05070c" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="sf-ring" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#0b0f17" />
          <stop offset=".7" stopColor="#1d2836" />
          <stop offset="1" stopColor="#8fb6c9" />
        </linearGradient>
        <linearGradient id="sf-flare" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#7fd3ff" stopOpacity="0" />
          <stop offset=".5" stopColor="#c9ecff" stopOpacity=".9" />
          <stop offset="1" stopColor="#7fd3ff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect width={W} height={H} fill="url(#sf-neb)" />
      {stars.map((s, i) => (
        <circle key={i} cx={s.x} cy={s.y} r={s.s} fill="#dfefff" opacity={s.o} />
      ))}
      {/* the station: a ring and a spine, lit from the right */}
      <g transform="translate(212 92) rotate(-18)">
        <ellipse rx="62" ry="20" fill="none" stroke="url(#sf-ring)" strokeWidth="7" />
        <ellipse rx="62" ry="20" fill="none" stroke="#c7e6f5" strokeOpacity=".35" strokeWidth=".6" />
        <rect x="-5" y="-34" width="10" height="68" rx="2" fill="#141c27" />
        <rect x="2" y="-34" width="3" height="68" fill="#7fa6ba" fillOpacity=".55" />
        {[-48, -24, 24, 48].map((x) => (
          <circle key={x} cx={x} cy={x / 6} r=".9" fill="#ffcf7a" />
        ))}
      </g>
      <g className="lp-art-flare">
        <rect x="80" y="78.6" width="260" height="1.6" fill="url(#sf-flare)" />
        <circle cx="210" cy="79.4" r="2.4" fill="#e8f7ff" />
      </g>
      {/* title card, trailer style */}
      <text x="160" y="128" textAnchor="middle" className="t-display" fontSize="27" fontWeight="800" letterSpacing="4.5" fill="#f2f0ea">
        THE LONG HEIST
      </text>
      <text x="160" y="144" textAnchor="middle" className="t-serif" fontStyle="italic" fontSize="9" fill="#b9c7d4">
        This winter, nothing is out of reach
      </text>
      {/* 2.39 letterbox */}
      <rect width={W} height="33" fill="#000" />
      <rect y={H - 33} width={W} height="33" fill="#000" />
      <g fill="#2a2d33">
        {Array.from({ length: 14 }, (_, i) => (
          <rect key={i} x={8 + i * 23} y="12" width="11" height="8" rx="1.5" />
        ))}
        {Array.from({ length: 14 }, (_, i) => (
          <rect key={i} x={8 + i * 23} y={H - 20} width="11" height="8" rx="1.5" />
        ))}
      </g>
    </Svg>
  );
}

/* ---------------------------------------------------------------- Math and science explainer */

function MathArt() {
  // y = x^2 on a dark canvas, the tangent at x = 1 (slope 2), and the derivative written above it
  const ox = 182;
  const oy = 160;
  const u = 34; // px per unit
  let d = "";
  for (let i = 0; i <= 60; i++) {
    const x = -1.85 + (i / 60) * 3.7;
    d += `${i ? "L" : "M"}${(ox + x * u).toFixed(1)} ${(oy - x * x * u * 0.9).toFixed(1)}`;
  }
  const ty = (x: number) => oy - (2 * x - 1) * u * 0.9;
  return (
    <Svg id="math-explainer" bg="#0f1830">
      <g stroke="#2a3a5e" strokeWidth=".5">
        {Array.from({ length: 17 }, (_, i) => (
          <line key={`v${i}`} x1={ox - 8 * u + i * u} x2={ox - 8 * u + i * u} y1="0" y2={H} />
        ))}
        {Array.from({ length: 7 }, (_, i) => (
          <line key={`h${i}`} y1={oy - i * u * 0.9} y2={oy - i * u * 0.9} x1="0" x2={W} />
        ))}
      </g>
      <g stroke="#8ea3c9" strokeWidth="1">
        <line x1="0" x2={W} y1={oy} y2={oy} />
        <line x1={ox} x2={ox} y1="8" y2={H} />
      </g>
      <path className="lp-art-draw" d={d} fill="none" stroke="#8fd3ff" strokeWidth="2.4" strokeLinecap="round" pathLength={1} />
      <line x1={ox + 0.15 * u} y1={ty(0.15)} x2={ox + 1.9 * u} y2={ty(1.9)} stroke="#ffb23e" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx={ox + u} cy={ty(1)} r="3.6" fill="#ffb23e" stroke="#0f1830" strokeWidth="1.4" />
      <text x={ox + u + 8} y={ty(1) + 12} className="t-serif" fontStyle="italic" fontSize="9" fill="#ffcf8a">
        slope 2
      </text>
      {/* d/dx x^2 = 2x */}
      <g className="t-serif" fill="#f1f4fb" fontStyle="italic">
        <text x="40" y="44" fontSize="14" textAnchor="middle">
          d
        </text>
        <line x1="30" x2="50" y1="49" y2="49" stroke="#f1f4fb" strokeWidth="1" />
        <text x="40" y="63" fontSize="14" textAnchor="middle">
          dx
        </text>
        <text x="56" y="58" fontSize="20">
          x
          <tspan fontSize="11" dy="-9">
            2
          </tspan>
          <tspan fontStyle="normal" dy="9" dx="6">
            =
          </tspan>
          <tspan fill="#ffcf8a" dx="6">
            2x
          </tspan>
        </text>
      </g>
      <text x="40" y="84" className="t-sans" fontSize="7.4" fill="#8ea3c9">
        Why the slope doubles
      </text>
    </Svg>
  );
}

/* ---------------------------------------------------------------- Character explainer */

function Explainer() {
  return (
    <Svg id="explainer" bg="#c9e7ef">
      <circle cx="58" cy="182" r="88" fill="#b5dce7" />
      <rect x="0" y="168" width={W} height="32" fill="#9fcfdc" />
      {/* the host: a friendly robot, pointing at the diagram */}
      <g transform="translate(78 64)">
        <line x1="22" y1="-2" x2="22" y2="-14" stroke="#2c3e50" strokeWidth="2.4" />
        <circle className="lp-art-blink" cx="22" cy="-16" r="4" fill="#ff7a59" />
        <rect x="0" y="0" width="44" height="34" rx="9" fill="#f7f3ea" stroke="#2c3e50" strokeWidth="2.4" />
        <rect x="6" y="6" width="32" height="20" rx="5" fill="#2c3e50" />
        <circle cx="16" cy="15" r="3" fill="#7ff0d6" />
        <circle cx="28" cy="15" r="3" fill="#7ff0d6" />
        <path d="M17 21q5 4 10 0" stroke="#7ff0d6" strokeWidth="1.6" fill="none" strokeLinecap="round" />
        <rect x="5" y="38" width="34" height="44" rx="8" fill="#ffb23e" stroke="#2c3e50" strokeWidth="2.4" />
        <circle cx="22" cy="56" r="5" fill="#f7f3ea" stroke="#2c3e50" strokeWidth="1.8" />
        <path d="M39 50l20-14" stroke="#2c3e50" strokeWidth="5" strokeLinecap="round" />
        <path d="M39 50l20-14" stroke="#f7f3ea" strokeWidth="2.4" strokeLinecap="round" />
        <path d="M5 50l-10 18" stroke="#2c3e50" strokeWidth="5" strokeLinecap="round" />
        <path d="M5 50l-10 18" stroke="#f7f3ea" strokeWidth="2.4" strokeLinecap="round" />
        <rect x="9" y="82" width="9" height="20" rx="3" fill="#2c3e50" />
        <rect x="26" y="82" width="9" height="20" rx="3" fill="#2c3e50" />
      </g>
      {/* diagram card: sun -> panel -> power */}
      <g transform="translate(150 58)">
        <rect width="142" height="78" rx="10" fill="#fffdf8" stroke="#2c3e50" strokeWidth="2" />
        <g transform="translate(24 40)">
          <circle r="11" fill="#ffc94a" stroke="#2c3e50" strokeWidth="1.8" />
          {Array.from({ length: 8 }, (_, i) => (
            <line key={i} x1="0" y1="-15" x2="0" y2="-19" stroke="#2c3e50" strokeWidth="1.8" strokeLinecap="round" transform={`rotate(${i * 45})`} />
          ))}
        </g>
        <path className="lp-art-flow" d="M44 40h14M98 40h14" stroke="#2c3e50" strokeWidth="2" strokeDasharray="3 3" />
        <path d="M56 36l4 4-4 4M110 36l4 4-4 4" stroke="#2c3e50" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <g transform="translate(64 26)">
          <path d="M6 0h26l-6 26H0z" fill="#3d6fb6" stroke="#2c3e50" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M19 0l-6 26M3 13h26" stroke="#9cc3f0" strokeWidth="1.2" />
        </g>
        <path d="M128 26l-10 15h7l-4 13 11-17h-7z" fill="#ffb23e" stroke="#2c3e50" strokeWidth="1.6" strokeLinejoin="round" />
      </g>
      {/* chapter header */}
      <g transform="translate(150 24)">
        <rect width="128" height="20" rx="10" fill="#2c3e50" />
        <text x="12" y="13.4" className="t-sans" fontSize="8.4" fontWeight="700" fill="#fff">
          Chapter 2
          <tspan fontWeight="400" fill="#c9e7ef" dx="5">
            Light into power
          </tspan>
        </text>
      </g>
    </Svg>
  );
}

/* ---------------------------------------------------------------- Faceless Short */

function Short() {
  return (
    <Svg id="short" bg="#14081f">
      <defs>
        <linearGradient id="sh-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3b0f5c" />
          <stop offset="1" stopColor="#0d0517" />
        </linearGradient>
        <linearGradient id="sh-sea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1b6f8a" />
          <stop offset="1" stopColor="#06233a" />
        </linearGradient>
        <filter id="sh-blur" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="14" />
        </filter>
        <clipPath id="sh-clip">
          <rect x="112" y="6" width="96" height="188" rx="12" />
        </clipPath>
      </defs>
      <rect width={W} height={H} fill="url(#sh-sea)" />
      <g filter="url(#sh-blur)" opacity=".55">
        <path d="M-20 214c40-80 10-130 70-176 44-34 110-20 116 18 6 34-34 40-46 20" fill="none" stroke="#e7609f" strokeWidth="40" strokeLinecap="round" />
        <circle cx="270" cy="40" r="50" fill="#ffd60a" opacity=".35" />
      </g>
      <rect width={W} height={H} fill="#07020f" opacity=".45" />
      {/* the vertical frame */}
      <g clipPath="url(#sh-clip)">
        <rect x="112" y="6" width="96" height="188" fill="url(#sh-sea)" />
        <g className="lp-art-punch" style={{ transformOrigin: "160px 110px" }}>
          <path d="M120 194c10-40 2-70 22-96 14-18 38-14 40 4 2 14-12 16-16 8" fill="none" stroke="#c0428a" strokeWidth="13" strokeLinecap="round" />
          <path d="M120 194c10-40 2-70 22-96 14-18 38-14 40 4 2 14-12 16-16 8" fill="none" stroke="#e7609f" strokeWidth="9" strokeLinecap="round" />
          {[0.1, 0.25, 0.4, 0.55, 0.7].map((t, i) => (
            <circle key={i} cx={128 + t * 40} cy={176 - t * 110} r="2.4" fill="#ffd0e4" />
          ))}
        </g>
        <rect x="120" y="14" width="80" height="2.4" rx="1.2" fill="#fff" fillOpacity=".25" />
        <rect x="120" y="14" width="34" height="2.4" rx="1.2" fill="#fff" />
      </g>
      <rect x="112" y="6" width="96" height="188" rx="12" fill="none" stroke="#fff" strokeOpacity=".18" />
      {/* word-by-word caption: two words on screen, the spoken one lit */}
      <g className="t-display" fontWeight="800" textAnchor="middle" fontSize="26">
        <text x="160" y="104" fill="#fff" stroke="#000" strokeWidth="5" paintOrder="stroke" strokeLinejoin="round">
          SOUNDS
        </text>
        <text className="lp-art-pop" x="160" y="130" fill="#ffd60a" stroke="#000" strokeWidth="5" paintOrder="stroke" strokeLinejoin="round">
          FAKE
        </text>
      </g>
    </Svg>
  );
}

/* ---------------------------------------------------------------- Music video */

function MusicVideo() {
  const r = rng(21);
  const towers = Array.from({ length: 18 }, (_, i) => ({ x: i * 19 - 6, w: 13 + r() * 10, h: 34 + r() * 70 }));
  const bars = Array.from({ length: 28 }, (_, i) => ({ x: 30 + i * 9.4, h: 4 + r() * 16, d: (r() * 0.8).toFixed(2), beat: i % 4 === 0 }));
  return (
    <Svg id="music-video" bg="#0b0620">
      <defs>
        <linearGradient id="mv-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#120a33" />
          <stop offset=".7" stopColor="#5a1a6e" />
          <stop offset="1" stopColor="#ff4fa3" />
        </linearGradient>
        <linearGradient id="mv-water" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#24104a" />
          <stop offset="1" stopColor="#07041a" />
        </linearGradient>
      </defs>
      <rect width={W} height="124" fill="url(#mv-sky)" />
      <circle cx="160" cy="112" r="34" fill="#ffd36e" opacity=".9" />
      <g fill="#ff4fa3" opacity=".9">
        {[100, 106, 112, 118].map((y) => (
          <rect key={y} x="120" y={y} width="80" height={1.6 + (y - 100) / 8} fill="#5a1a6e" />
        ))}
      </g>
      <g fill="#0f0726">
        {towers.map((t, i) => (
          <rect key={i} x={t.x} y={124 - t.h} width={t.w} height={t.h} />
        ))}
      </g>
      <g fill="#5ef2ff" opacity=".8">
        {towers.map((t, i) => (i % 3 === 0 ? <rect key={i} x={t.x + 3} y={124 - t.h + 6} width="2" height="2" /> : null))}
      </g>
      <rect y="124" width={W} height="76" fill="url(#mv-water)" />
      <g opacity=".35">
        {towers.map((t, i) => (
          <rect key={i} x={t.x} y="124" width={t.w} height={t.h * 0.4} fill="#ff4fa3" opacity={i % 2 ? 0.4 : 0.7} />
        ))}
      </g>
      <text x="160" y="150" textAnchor="middle" className="t-serif" fontStyle="italic" fontSize="13" fill="#fff" fillOpacity=".95">
        we were electric
      </text>
      {/* the beat: bars that bounce on hover, every fourth one a downbeat */}
      <g transform="translate(0 186)">
        {bars.map((b, i) => (
          <rect key={i} className="lp-art-eq" style={{ animationDelay: `${b.d}s` }} x={b.x} y={-b.h} width="5" height={b.h} rx="1.4" fill={b.beat ? "#5ef2ff" : "#ff4fa3"} opacity={b.beat ? 1 : 0.75} />
        ))}
      </g>
    </Svg>
  );
}

/* ---------------------------------------------------------------- Photo slideshow */

function Photo({ x, y, rot, children, cls = "" }: { x: number; y: number; rot: number; children: ReactNode; cls?: string }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rot})`}>
      <g className={cls}>
        <rect x="-3" y="2" width="112" height="92" fill="#000" opacity=".25" />
        <rect width="110" height="90" fill="#fbf7ef" />
        <svg x="6" y="6" width="98" height="68" viewBox="0 0 98 68">
          {children}
        </svg>
      </g>
    </g>
  );
}

function Slideshow() {
  return (
    <Svg id="slideshow" bg="#3a2a22">
      <defs>
        <radialGradient id="ss-lamp" cx=".35" cy=".3" r=".8">
          <stop offset="0" stopColor="#c98a56" stopOpacity=".55" />
          <stop offset="1" stopColor="#1e140f" stopOpacity=".2" />
        </radialGradient>
        <linearGradient id="ss-dusk" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f6b26b" />
          <stop offset="1" stopColor="#e2725b" />
        </linearGradient>
        <linearGradient id="ss-day" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#a9d3e6" />
          <stop offset="1" stopColor="#f1e3c8" />
        </linearGradient>
      </defs>
      <rect width={W} height={H} fill="url(#ss-lamp)" />
      <Photo x={44} y={52} rot={-9}>
        <rect width="98" height="68" fill="url(#ss-day)" />
        <path d="M0 52l26-24 18 14 20-26 34 36v16H0z" fill="#6b8f71" />
        <path d="M0 60l40-12 58 8v12H0z" fill="#4b6b53" />
      </Photo>
      <Photo x={176} y={44} rot={8}>
        <rect width="98" height="68" fill="#f3d9c2" />
        {[0, 1, 2, 3, 4].map((i) => (
          <circle key={i} cx={12 + i * 19} cy={20 + (i % 2) * 8} r="10" fill="#f2a7b8" opacity=".85" />
        ))}
        <path d="M0 50h98v18H0z" fill="#c4886a" />
        <path d="M20 68V40M78 68V36" stroke="#6b4a3a" strokeWidth="3" />
      </Photo>
      <Photo x={110} y={60} rot={-1} cls="lp-art-lift">
        <rect width="98" height="68" fill="url(#ss-dusk)" />
        <circle cx="66" cy="30" r="10" fill="#fff1c9" />
        <path d="M0 50l30-18 22 10 18-8 28 14v20H0z" fill="#8a4a52" />
        {/* a torii gate on the hill */}
        <g fill="#3a1d22">
          <rect x="22" y="22" width="34" height="3.4" rx="1" />
          <rect x="25" y="28" width="28" height="2.4" />
          <rect x="28" y="24" width="3" height="28" />
          <rect x="47" y="24" width="3" height="28" />
        </g>
      </Photo>
      <text x="160" y="182" textAnchor="middle" className="t-serif" fontStyle="italic" fontSize="15" fill="#fbf1e2">
        Kyoto, in spring
      </text>
    </Svg>
  );
}

/* ---------------------------------------------------------------- Kinetic typography */

function Kinetic() {
  return (
    <Svg id="kinetic" bg="#ffb23e">
      <g className="t-display" fill="#140b02">
        <text className="lp-art-slide lp-art-slide-1" x="34" y="78" fontSize="64" fontWeight="800" letterSpacing="-1">
          START
        </text>
        <text className="lp-art-slide lp-art-slide-2" x="36" y="122" fontSize="44" fontWeight="400">
          small.
        </text>
        <g className="lp-art-slide lp-art-slide-3">
          <rect x="150" y="96" width="138" height="34" fill="#140b02" />
          <text x="157" y="124" fontSize="30" fontWeight="800" fill="#ffb23e">
            then grow
          </text>
        </g>
        <text className="lp-art-slide lp-art-slide-4" x="36" y="172" fontSize="38" fontWeight="300" letterSpacing="2" fill="#140b02" fillOpacity=".55">
          every single day
        </text>
      </g>
    </Svg>
  );
}

/* ---------------------------------------------------------------- Podcast clip */

function Podcast() {
  const r = rng(4);
  const wave = Array.from({ length: 44 }, (_, i) => {
    const env = Math.sin((i / 43) * Math.PI) ** 0.6;
    return { x: 44 + i * 5.4, h: 3 + (0.3 + 0.7 * r()) * env * 26, d: (r() * 0.9).toFixed(2) };
  });
  return (
    <Svg id="podcast" bg="#121015">
      <defs>
        <radialGradient id="pc-rim" cx=".5" cy="0" r=".9">
          <stop offset="0" stopColor="#ffb23e" stopOpacity=".22" />
          <stop offset="1" stopColor="#121015" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width={W} height={H} fill="url(#pc-rim)" />
      {/* two speaker cards; the one talking is lit */}
      {[
        { x: 96, i: "MA", c: "#7ab8ff", on: false },
        { x: 224, i: "JO", c: "#ffb23e", on: true },
      ].map((s) => (
        <g key={s.i} transform={`translate(${s.x} 66)`}>
          {s.on && <circle r="33" fill={s.c} opacity=".18" />}
          <circle r="27" fill="#1f1b24" stroke={s.c} strokeWidth={s.on ? 3 : 1.4} strokeOpacity={s.on ? 1 : 0.55} />
          <text y="7" textAnchor="middle" className="t-display" fontSize="21" fontWeight="700" fill={s.on ? "#fff" : "#b7b2bd"}>
            {s.i}
          </text>
        </g>
      ))}
      <g transform="translate(0 132)">
        {wave.map((b, i) => (
          <rect key={i} className="lp-art-eq lp-art-eq-mid" style={{ animationDelay: `${b.d}s` }} x={b.x} y={-b.h / 2} width="3" height={b.h} rx="1.5" fill={i < 26 ? "#ffb23e" : "#5a5260"} />
        ))}
      </g>
      <text x="160" y="180" textAnchor="middle" className="t-sans" fontSize="11" fill="#f3efe8">
        <tspan fill="#ffb23e" fontWeight="700">
          Jo:{" "}
        </tspan>
        The office isn't coming back.
      </text>
    </Svg>
  );
}

/* ---------------------------------------------------------------- Product demo */

function Demo() {
  const bars = [30, 44, 26, 52, 40, 60, 48];
  return (
    <Svg id="demo" bg="#e9edf4">
      <defs>
        <linearGradient id="dm-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f3f5fa" />
          <stop offset="1" stopColor="#d9e0ec" />
        </linearGradient>
      </defs>
      <rect width={W} height={H} fill="url(#dm-bg)" />
      <g className="lp-art-zoom">
        {/* browser */}
        <rect x="30" y="22" width="260" height="168" rx="8" fill="#fff" stroke="#c9d1de" />
        <path d="M30 30a8 8 0 0 1 8-8h244a8 8 0 0 1 8 8v8H30z" fill="#f2f4f8" />
        {[40, 48, 56].map((x) => (
          <circle key={x} cx={x} cy="30" r="2.4" fill="#c9d1de" />
        ))}
        <rect x="120" y="26" width="80" height="8" rx="4" fill="#e3e8f0" />
        {/* sidebar */}
        <rect x="30" y="38" width="52" height="152" fill="#f7f8fb" />
        {[50, 62, 74, 86].map((y, i) => (
          <rect key={y} x="38" y={y} width={i === 1 ? 34 : 28} height="5" rx="2.5" fill={i === 1 ? "#5b7cfa" : "#d5dbe6"} />
        ))}
        {/* cards */}
        <rect x="92" y="48" width="58" height="30" rx="5" fill="#f3f6ff" />
        <rect x="156" y="48" width="58" height="30" rx="5" fill="#f3f6ff" />
        <rect x="98" y="54" width="22" height="4" rx="2" fill="#aab6cc" />
        <rect x="98" y="63" width="34" height="8" rx="2" fill="#253250" />
        <rect x="162" y="54" width="22" height="4" rx="2" fill="#aab6cc" />
        <rect x="162" y="63" width="28" height="8" rx="2" fill="#253250" />
        {/* chart */}
        <rect x="92" y="86" width="122" height="94" rx="5" fill="#fbfcff" stroke="#edf0f6" />
        {bars.map((h, i) => (
          <rect key={i} x={102 + i * 15.4} y={172 - h} width="9" height={h} rx="2" fill={i === 5 ? "#5b7cfa" : "#c9d4f6"} />
        ))}
        {/* the button the demo zooms to */}
        <rect x="226" y="48" width="54" height="18" rx="9" fill="#5b7cfa" />
        <text x="253" y="60" textAnchor="middle" className="t-sans" fontSize="7.4" fontWeight="700" fill="#fff">
          Export
        </text>
        <rect x="226" y="74" width="54" height="106" rx="5" fill="#f7f8fb" />
        {/* callout */}
        <rect className="lp-art-ping" x="219" y="41" width="68" height="32" rx="14" fill="none" stroke="#ff9e1b" strokeWidth="2.2" />
        <g transform="translate(176 96)">
          <path d="M60 -22l6 -8 4 8z" fill="#1d2437" />
          <rect x="0" y="-22" width="104" height="24" rx="6" fill="#1d2437" />
          <text x="10" y="-6.6" className="t-sans" fontSize="8.4" fontWeight="600" fill="#fff">
            Export in one tap
          </text>
        </g>
        <path d="M262 64l0 15 4-4 3 7 3-1.4-3-7 6-.4z" fill="#1d2437" stroke="#fff" strokeWidth="1.2" strokeLinejoin="round" />
      </g>
    </Svg>
  );
}

/* ---------------------------------------------------------------- Ad (the poster under its real sample) */

function Ad() {
  return (
    <Svg id="ad" bg="#1b1410">
      <defs>
        <linearGradient id="ad-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2c1d12" />
          <stop offset="1" stopColor="#120c08" />
        </linearGradient>
        <linearGradient id="ad-bag" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#7d4a26" />
          <stop offset=".5" stopColor="#b8763f" />
          <stop offset="1" stopColor="#6b3d1f" />
        </linearGradient>
        <radialGradient id="ad-spot" cx=".5" cy=".2" r=".6">
          <stop offset="0" stopColor="#ffd9a0" stopOpacity=".5" />
          <stop offset="1" stopColor="#ffd9a0" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width={W} height={H} fill="url(#ad-bg)" />
      <rect width={W} height={H} fill="url(#ad-spot)" />
      <ellipse cx="160" cy="170" rx="70" ry="10" fill="#000" opacity=".5" />
      <path d="M128 60h64l6 108h-76z" fill="url(#ad-bag)" />
      <path d="M128 60l8-10h48l8 10z" fill="#5a3218" />
      <rect x="138" y="96" width="44" height="34" rx="3" fill="#f3e6cf" />
      <text x="160" y="116" textAnchor="middle" className="t-display" fontSize="12" fontWeight="800" fill="#3a2212">
        ROAST
      </text>
      <text x="160" y="40" textAnchor="middle" className="t-display" fontSize="22" fontWeight="800" fill="#fff">
        Your new morning
      </text>
      <rect x="124" y="178" width="72" height="16" rx="8" fill="#ffb23e" />
      <text x="160" y="189" textAnchor="middle" className="t-sans" fontSize="7.6" fontWeight="700" fill="#1a1006">
        Order today
      </text>
    </Svg>
  );
}

/* ---------------------------------------------------------------- a format the page doesn't know yet */

function Generic({ name }: { name: string }) {
  // the brand's hand-frame: two corners around the name
  return (
    <Svg id="generic" bg="#0c1120">
      <g fill="none" stroke="#ffb23e" strokeWidth="5" strokeLinecap="square">
        <path d="M70 80V54h26" />
        <path d="M250 120v26h-26" />
      </g>
      <text x="160" y="108" textAnchor="middle" className="t-display" fontSize="22" fontWeight="700" fill="#eef0f6">
        {name}
      </text>
    </Svg>
  );
}

const ART: Record<string, () => ReactNode> = {
  ad: Ad,
  documentary: Documentary,
  cartoon: Cartoon,
  "short-film": ShortFilm,
  "math-explainer": MathArt,
  explainer: Explainer,
  short: Short,
  "music-video": MusicVideo,
  slideshow: Slideshow,
  kinetic: Kinetic,
  podcast: Podcast,
  demo: Demo,
};

/** Formats with their own drawn preview (landing.test.ts: every core format has one). */
export const ART_IDS = Object.keys(ART);

export function FormatArt({ id, name }: { id: string; name: string }) {
  const A = ART[id];
  return A ? <A /> : <Generic name={name} />;
}
